import * as React from 'react';
import {useDispatch} from 'react-redux';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {change} from 'redux-form';

import {Button} from 'shells/button/button';
import {SectionLabel} from 'shells/typography/section-label';

import {useGetPeepRelatedLeadStats} from '../entity-details/sources/hooks/use-get-peep-related-lead-stats';
import {LEADS_FORM_KEY} from '../entity-form/forms/lead-form-connected';

import {PEEP_RELATED_LEAD_STATS_UPDATE_EVENT_NAME} from './helpers';
import {PeepRelatedLeadStats} from './peep-related-lead-stats';

import styles from './peep-related-leads.module.css';

type OwnProps = {
    id: string;
};

type Props = ContextRouter & OwnProps;

export function PeepRelatedLeadsGraphQL(props: Props) {
    const {isLoading, peep, errorMessage, refetch} = useGetPeepRelatedLeadStats(props.id);
    const dispatch = useDispatch();
    const buttonRef = React.useRef(null);
    const hasChildren = peep && peep.childLeads && peep.childLeads.subsidiaryStats; // Subsidiary stats is null when there are no children

    const onCreateNewLead = React.useCallback(() => {
        props.history.push({hash: '/create/leads', state: {modal: true}});
        // Let's make sure our form is instantiated
        // before trying to change its values
        let tempPeep = null;

        // For our richer contact field on the lead create modal, we'll need to
        // pass up more info to fill our ContactCard components
        if (peep.type === 'contacts' && NutClientConfig.hasEnhancedLeadCreateModal) {
            const phoneNumber =
                peep &&
                peep.phones &&
                peep.phones.primary &&
                peep.phones.primary.value &&
                peep.phones.primary.value.numberFormatted;
            const email =
                peep &&
                peep.emailAddresses &&
                peep.emailAddresses.primary &&
                peep.emailAddresses.primary.value &&
                peep.emailAddresses.primary.value.display;

            tempPeep = {
                id: peep.id,
                type: peep.type,
                value: peep.name,
                primaryPhoneText: phoneNumber,
                primaryEmailText: email,
                jobTitle: peep.jobTitle || '',
                avatarUrl: peep.avatarUrl,
                description: peep.description,
            };
        } else {
            tempPeep = {
                id: peep.id,
                type: peep.type,
                value: peep.name,
                description: peep.description,
                tertiaryInfo:
                    peep &&
                    peep.postalAddresses &&
                    peep.postalAddresses.primary &&
                    peep.postalAddresses.primary.value &&
                    peep.postalAddresses.primary.value.region,
                avatarUrl: peep.avatarUrl,
            };
        }
        setTimeout(() => {
            dispatch(change(LEADS_FORM_KEY, `links.${peep.type}`, [tempPeep]));
        }, 500);
    }, [peep, dispatch, props.history]);

    const handleCreateLead = React.useCallback(() => {
        if (peep) {
            onCreateNewLead();
        }
    }, [peep, onCreateNewLead]);

    React.useEffect(() => {
        // This is so we can call "click()" via an external javascript source
        // and still have the handler get called. Calling click() will not fire
        // the `onClick` prop
        if (buttonRef && buttonRef.current) {
            // @ts-expect-error
            buttonRef.current.addEventListener('click', handleCreateLead);
        }

        const button = buttonRef.current;

        return () => {
            if (button) {
                // @ts-expect-error
                button.removeEventListener('click', handleCreateLead);
            }
        };
    }, [handleCreateLead]);

    React.useEffect(() => {
        // Allows related leads stats to be updated by components that are mounted separately
        // @ts-expect-error
        window.addEventListener(PEEP_RELATED_LEAD_STATS_UPDATE_EVENT_NAME, refetch);

        return () => {
            // @ts-expect-error
            window.removeEventListener(PEEP_RELATED_LEAD_STATS_UPDATE_EVENT_NAME, refetch);
        };
    }, [refetch]);

    return (
        <div className={styles['peep-related-leads']}>
            <div>
                <SectionLabel>Leads</SectionLabel>
                <PeepRelatedLeadStats
                    peep={peep}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                />
            </div>
            {hasChildren ? (
                <div>
                    <SectionLabel>Child leads</SectionLabel>
                    <PeepRelatedLeadStats
                        peep={peep}
                        isLoading={isLoading}
                        errorMessage={errorMessage}
                        showChildLeadInfo={true}
                    />
                </div>
            ) : null}
            <div className={styles['create-lead']}>
                <Button getButtonRef={buttonRef} onClick={handleCreateLead} variant='text-primary'>
                    Create a lead
                </Button>
            </div>
        </div>
    );
}

// @ts-expect-error
export const PeepRelatedLeads = withRouter<OwnProps>(PeepRelatedLeadsGraphQL);
